import React, { FC, memo, useCallback, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { WarehouseWithOccupancyStatistic } from 'entities/Warehouse';
import { createSearchParams, Link, useNavigate } from 'react-router-dom';
import { Button } from 'shared/ui/Button';
import { ReactComponent as KeyIcon } from 'shared/assets/icons/KeyIcon.svg';
import { ReactComponent as DocumentIcon } from 'shared/assets/icons/DocumentIcon.svg';
import { ReactComponent as UploadIcon } from 'shared/assets/icons/UploadIcon.svg';
import { ReactComponent as SettingsIcon } from 'shared/assets/icons/SettingsIcon.svg';
import { ProgressBar } from 'shared/ui/ProgressBar';
import { ContextMenu } from 'shared/ui/ContextMenu';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useQueryString } from 'shared/utils/helpers/convertQueryString';
import { warehouseStatisticCardActions } from '../model/slice/warehouseStatisticCardSlice';
import { useRole } from 'entities/User';
import { MimeTypes, uploadModalActions } from 'features/UploadModal';

interface WarehouseStatisticCardProps {
  warehouseData: WarehouseWithOccupancyStatistic;
}

export const WarehouseStatisticCard: FC<WarehouseStatisticCardProps> = memo((props) => {
  const {
    warehouseData: { warehouseId, name, address, occupiedBoxesAmount, activeBoxesAmount, occupiedSquare, activeSquare },
  } = props;

  const dispatch = useAppDispatch();

  const { t } = useAppTranslation('warehouses');

  const { isManagement, isPartner } = useRole();

  const navigate = useNavigate();

  const { getEncodedString } = useQueryString();

  const boxesPercent = roundNumber((occupiedBoxesAmount / activeBoxesAmount) * 100, 0);
  const squarePercent = roundNumber((occupiedSquare / activeSquare) * 100, 0);

  const openUploadModal = useCallback((): void => {
    dispatch(
      uploadModalActions.openModal({
        title: t('Upload warehouse boxes'),
        description: t('Upload CSV or EXCEL file!'),
        submitArgs: { warehouseId },
        allowedFormats: [MimeTypes.CSV, MimeTypes.EXCEL_XLS, MimeTypes.EXCEL_XLSX],
        draggerDescription: t('Excel file size must be no more than 10MB'),
      }),
    );
  }, [dispatch, t, warehouseId]);

  const contextMenuItems = useMemo(
    () => [
      { key: '1', label: <Link to="#">{t('Settings')}</Link>, icon: <SettingsIcon /> },
      { key: '2', label: <Link to="#">{t('Reports')}</Link>, icon: <DocumentIcon /> },
      {
        key: '3',
        label: (
          <Button
            icon={<UploadIcon className="w-6 h-6" />}
            iconPosition="prev"
            fontColor="text-primary"
            theme="clear"
            onClick={openUploadModal}
          >
            {t('Import boxes')}
          </Button>
        ),
      },
    ],
    [openUploadModal, t],
  );

  const goToBoxesPage = useCallback(() => {
    const encodedWarehouse = getEncodedString({ selectedWarehouseId: { value: warehouseId, label: name, title: t('Warehouse') } });

    // base64 may contain "+", so the query string must be URL-encoded
    navigate({ pathname: AppRoutes.BOXES, search: createSearchParams(encodedWarehouse).toString() });
  }, [getEncodedString, name, navigate, t, warehouseId]);

  const goToWarehousePage = useCallback(() => {
    navigate(`${AppRoutes.WAREHOUSES}/${warehouseId}`);
  }, [navigate, warehouseId]);

  const goToHistoryPage = useCallback(() => {
    navigate(`${AppRoutes.WAREHOUSES}/${warehouseId}/history`);
  }, [navigate, warehouseId]);

  const openDoorsModal = useCallback((): void => {
    dispatch(warehouseStatisticCardActions.setDoorsModalOpened(warehouseId));
  }, [dispatch, warehouseId]);

  return (
    <div className="flex flex-col w-[350px] bg-secondaryLight py-10 px-6 rounded-lg">
      <div className="bg-white py-7 px-6 rounded-lg space-y-4 cursor-pointer" role="button" tabIndex={0} onClick={goToWarehousePage}>
        <div className="flex justify-between items-start gap-2">
          <div className="min-h-12">
            <div className="font-semibold text-lg">{name}</div>
            {address && <div className="text-sm text-primaryLight">{address}</div>}
          </div>
          {isManagement && (
            <div
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <ContextMenu items={contextMenuItems} />
            </div>
          )}
        </div>
        <ProgressBar
          percent={boxesPercent}
          description={t('{{occupiedBoxesAmount}} boxes out of {{totalBoxesAmount}} are occupied', {
            occupiedBoxesAmount,
            totalBoxesAmount: activeBoxesAmount,
          })}
        />
        <ProgressBar
          percent={squarePercent}
          description={t('{{occupiedSquare}} m² out of {{totalSquare}} m² are occupied', {
            occupiedSquare: formatDecimal(roundNumber(occupiedSquare)),
            totalSquare: formatDecimal(roundNumber(activeSquare)),
          })}
        />
        <div className="space-y-2.5">
          <Button theme="secondary" onClick={goToBoxesPage}>
            {t('Boxes')}
          </Button>
          <Button icon={<KeyIcon className="stroke-white" />} iconPosition="prev" onClick={openDoorsModal}>
            {t('Doors')}
          </Button>
          {(isManagement || isPartner) && (
            <Button theme="secondary" onClick={goToHistoryPage}>
              {t('Openings history')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});
